/**
 * Interactive Preview demo of the new customer experience.
 * Mounted at /preview/experience. Every button uses in-memory sample data — nothing
 * touches the real backend, no payments are made, no emails are sent, no credits are
 * deducted from real accounts. Reset the demo any time with the "Reset demo" button
 * at the top-right of the scenario panel.
 *
 * Covers all flows in the spec:
 *   - Locked project card + wallet button + purchase pop-up
 *   - $99 single-report purchase (tied to selected project — no wallet credit added)
 *   - $375 five-credit bundle purchase (wallet + 5 credits, credits never expire)
 *   - "Unlock with 1 report credit" + confirmation before deduction
 *   - Report-ready state on the project card + Download PDF / Download CSV
 *   - Account dropdown (Profile / Wallet / Buy credits / My unlocked reports / Settings / Sign out)
 *   - My Unlocked Reports list (newest-first, EN+FR under one version)
 *   - Language switcher for existing report versions
 *   - Report version snapshotting + "Updated, new report required"
 *   - Project-update warning modal
 *   - Report deletion confirmation (deletes all language copies together)
 *   - Regeneration confirmation + regenerating state + retry-on-fail
 *   - Receipt email preview + PDF receipt preview
 *   - Report-ready email preview + Translated-report email preview + Regenerated email
 */
import { useMemo, useReducer, useState } from "react";
import { Link } from "react-router-dom";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import {
  Wallet, Lock, CheckCircle2, Clock, Download, Trash2, RotateCw, ChevronDown,
  User, LogOut, FileText, X, AlertCircle, Sparkles, Mail, ArrowRight, RefreshCw,
  Globe, ShieldCheck,
} from "lucide-react";

/* ═══════════════════════════════════════════════════════════════════════════
 *  SAMPLE DATA
 * ═══════════════════════════════════════════════════════════════════════════ */

const SAMPLE_CUSTOMER = {
  name: "Marco Rossi",
  email: "marco@futureenergyventure.com",
  initial: "M",
};

const iso = (offsetDays = 0) => {
  const d = new Date();
  d.setDate(d.getDate() + offsetDays);
  return d.toISOString().slice(0, 10);
};

const initialState = {
  wallet: 0,
  projects: [
    {
      id: "p1",
      name: "Lagos Textile Factory",
      country: "Nigeria",
      status: "locked",
      lastEditedAt: iso(-3),
      liveSnapshotKey: "lagos-v1",
      reports: [],
    },
    {
      id: "p2",
      name: "Nairobi Mining Site",
      country: "Kenya",
      status: "unlocked",
      lastEditedAt: iso(-10),
      liveSnapshotKey: "nairobi-v1",
      reports: [
        {
          version: 1,
          unlockedAt: iso(-10),
          snapshotKey: "nairobi-v1",
          projectName: "Nairobi Mining Site",
          country: "Kenya",
          languages: { en: "ready", fr: null },
          status: "ready",
        },
      ],
    },
    {
      id: "p3",
      name: "Cairo Solar Farm",
      country: "Egypt",
      status: "updated", // has a report but live config changed
      lastEditedAt: iso(-1),
      liveSnapshotKey: "cairo-v2",
      reports: [
        {
          version: 1,
          unlockedAt: iso(-20),
          snapshotKey: "cairo-v1",
          projectName: "Cairo Solar Farm",
          country: "Egypt",
          languages: { en: "ready", fr: "ready" },
          status: "ready",
        },
      ],
    },
  ],
  language: "en", // EN or FR
  // ephemeral state
  activeModal: null,      // { kind: "purchase" | "confirm-unlock" | "update-warning" | "delete-report" | "regenerate" | "receipt" | "ready-email" | "translated-email" | "regen-email", ... }
  accountOpen: false,
  view: "projects",       // "projects" | "reports" | "project-detail"
  activeProjectId: null,
  toast: null,
};

/* ═══════════════════════════════════════════════════════════════════════════
 *  REDUCER (state machine for the demo journey)
 * ═══════════════════════════════════════════════════════════════════════════ */

function reducer(s, a) {
  switch (a.type) {
    case "RESET": return initialState;
    case "SET_LANG": return { ...s, language: a.language };
    case "GO": return { ...s, view: a.view, activeProjectId: a.projectId ?? null, accountOpen: false };
    case "TOGGLE_ACCOUNT": return { ...s, accountOpen: !s.accountOpen };
    case "TOAST": return { ...s, toast: a.msg };
    case "CLEAR_TOAST": return { ...s, toast: null };
    case "OPEN_MODAL": return { ...s, activeModal: a.modal };
    case "CLOSE_MODAL": return { ...s, activeModal: null };

    case "BUY_SINGLE": {
      // $99 — auto-unlock the selected project, no wallet credit
      const pid = a.projectId;
      const nextProjects = s.projects.map((p) => {
        if (p.id !== pid) return p;
        const nextVersion = (p.reports?.length || 0) + 1;
        const newReport = {
          version: nextVersion,
          unlockedAt: iso(0),
          snapshotKey: p.liveSnapshotKey,
          projectName: p.name,
          country: p.country,
          languages: { en: s.language === "en" ? "ready" : null, fr: s.language === "fr" ? "ready" : null },
          status: "ready",
        };
        return { ...p, status: "unlocked", reports: [...(p.reports || []), newReport] };
      });
      return { ...s, projects: nextProjects, activeModal: { kind: "receipt", purchaseType: "single", amount: 99, projectId: pid, creditsAdded: 0 } };
    }

    case "BUY_BUNDLE": {
      // $375 for 5 credits
      return { ...s, wallet: s.wallet + 5, activeModal: { kind: "receipt", purchaseType: "bundle", amount: 375, creditsAdded: 5 } };
    }

    case "USE_CREDIT": {
      // Deduct one credit + unlock the target project
      const pid = a.projectId;
      const nextProjects = s.projects.map((p) => {
        if (p.id !== pid) return p;
        const nextVersion = (p.reports?.length || 0) + 1;
        const newReport = {
          version: nextVersion,
          unlockedAt: iso(0),
          snapshotKey: p.liveSnapshotKey,
          projectName: p.name,
          country: p.country,
          languages: { en: s.language === "en" ? "ready" : null, fr: s.language === "fr" ? "ready" : null },
          status: "ready",
        };
        return { ...p, status: "unlocked", reports: [...(p.reports || []), newReport] };
      });
      return { ...s, wallet: s.wallet - 1, projects: nextProjects, activeModal: { kind: "ready-email", projectId: pid } };
    }

    case "EDIT_PROJECT": {
      // Simulate a calc-affecting edit → bump snapshot key so live drifts from last report
      const pid = a.projectId;
      const nextProjects = s.projects.map((p) => {
        if (p.id !== pid) return p;
        const bumped = `${p.liveSnapshotKey.replace(/-v\d+$/, "")}-v${Math.floor(Math.random() * 100)}`;
        return { ...p, status: p.reports?.length ? "updated" : p.status, liveSnapshotKey: bumped, lastEditedAt: iso(0) };
      });
      return { ...s, projects: nextProjects, activeModal: null, toast: "Project updated — a new report credit is needed for the updated version." };
    }

    case "REQUEST_TRANSLATION": {
      // Add "preparing" state to the missing language of a specific report
      const { projectId, version, targetLang } = a;
      const nextProjects = s.projects.map((p) => {
        if (p.id !== projectId) return p;
        return {
          ...p,
          reports: p.reports.map((r) => (r.version === version ? { ...r, languages: { ...r.languages, [targetLang]: "preparing" } } : r)),
        };
      });
      return { ...s, projects: nextProjects, toast: `${targetLang === "fr" ? "French" : "English"} version preparing…` };
    }

    case "TRANSLATION_READY": {
      const { projectId, version, targetLang } = a;
      const nextProjects = s.projects.map((p) => {
        if (p.id !== projectId) return p;
        return {
          ...p,
          reports: p.reports.map((r) => (r.version === version ? { ...r, languages: { ...r.languages, [targetLang]: "ready" } } : r)),
        };
      });
      return { ...s, projects: nextProjects, activeModal: { kind: "translated-email", projectId, version, targetLang } };
    }

    case "DELETE_REPORT": {
      // Remove the file copies but keep a deleted-marker entry so entitlement is preserved
      const { projectId, version } = a;
      const nextProjects = s.projects.map((p) => {
        if (p.id !== projectId) return p;
        return {
          ...p,
          reports: p.reports.map((r) => (r.version === version ? { ...r, languages: { en: "deleted", fr: "deleted" }, status: "deleted" } : r)),
        };
      });
      return { ...s, projects: nextProjects, activeModal: null, toast: `Report version ${version} deleted — you can regenerate it for free anytime.` };
    }

    case "REGENERATE_START": {
      const { projectId, version } = a;
      const nextProjects = s.projects.map((p) => {
        if (p.id !== projectId) return p;
        return {
          ...p,
          reports: p.reports.map((r) => (r.version === version ? { ...r, status: "regenerating" } : r)),
        };
      });
      return { ...s, projects: nextProjects, activeModal: null, toast: `Regenerating report version ${version}…` };
    }

    case "REGENERATE_DONE": {
      const { projectId, version } = a;
      const nextProjects = s.projects.map((p) => {
        if (p.id !== projectId) return p;
        return {
          ...p,
          reports: p.reports.map((r) => (r.version === version ? { ...r, status: "ready", regeneratedAt: iso(0), languages: { ...r.languages, [s.language]: "ready" } } : r)),
        };
      });
      return { ...s, projects: nextProjects, activeModal: { kind: "regen-email", projectId, version } };
    }

    case "DELETE_PROJECT": {
      // Live project removed, but paid reports live on under a synthetic archive
      const pid = a.projectId;
      const nextProjects = s.projects.map((p) => (p.id === pid ? { ...p, status: "archived-liveremoved" } : p));
      return { ...s, projects: nextProjects, activeModal: null, toast: "Live project removed — paid reports remain available in My unlocked reports." };
    }

    default: return s;
  }
}

/* ═══════════════════════════════════════════════════════════════════════════
 *  MAIN VIEW
 * ═══════════════════════════════════════════════════════════════════════════ */

export const PreviewExperience = () => {
  const [state, dispatch] = useReducer(reducer, initialState);
  const activeProject = useMemo(
    () => state.projects.find((p) => p.id === state.activeProjectId) || null,
    [state.activeProjectId, state.projects]
  );

  return (
    <div className="App">
      <div className="w-full py-2 px-6" style={{ background: "#FBE59A", borderBottom: "1px solid #E7CB58" }}>
        <div className="max-w-[1280px] mx-auto text-xs flex items-center justify-between gap-4" style={{ color: "#5B4A00" }}>
          <div className="flex items-center gap-2"><Sparkles size={12} /> <b>Preview demo</b> — all buttons are mocked. No real payments, credits or emails.</div>
          <button onClick={() => dispatch({ type: "RESET" })} className="underline font-medium" data-testid="preview-reset">Reset demo</button>
        </div>
      </div>

      <PreviewHeader state={state} dispatch={dispatch} />

      <div className="max-w-[1280px] mx-auto px-6 lg:px-10 py-10">
        {state.view === "projects" && <ProjectsView state={state} dispatch={dispatch} />}
        {state.view === "reports"  && <UnlockedReportsView state={state} dispatch={dispatch} />}
        {state.view === "project-detail" && activeProject && <ProjectDetailView project={activeProject} state={state} dispatch={dispatch} />}
      </div>

      {state.toast && (
        <div className="fixed bottom-6 right-6 z-50 p-4 rounded-md shadow-lg max-w-sm text-sm" style={{ background: "var(--charcoal)", color: "var(--white)" }} data-testid="demo-toast">
          <div className="flex items-start gap-3">
            <div className="flex-1">{state.toast}</div>
            <button onClick={() => dispatch({ type: "CLEAR_TOAST" })}><X size={14} /></button>
          </div>
        </div>
      )}

      <Modals state={state} dispatch={dispatch} />

      <ScenarioPanel state={state} dispatch={dispatch} />
      <Footer />
    </div>
  );
};

export default PreviewExperience;

/* ═══════════════════════════════════════════════════════════════════════════
 *  HEADER — wallet pill + account dropdown
 * ═══════════════════════════════════════════════════════════════════════════ */

const PreviewHeader = ({ state, dispatch }) => (
  <header className="border-b" style={{ borderColor: "var(--hairline)", background: "var(--white)" }}>
    <div className="max-w-[1280px] mx-auto px-6 lg:px-10 h-16 flex items-center justify-between gap-4">
      <div className="flex items-center gap-8">
        <button onClick={() => dispatch({ type: "GO", view: "projects" })} className="font-display text-xl">Power Configurator</button>
        <nav className="hidden md:flex items-center gap-6 text-sm">
          <button onClick={() => dispatch({ type: "GO", view: "projects" })} className={state.view === "projects" ? "font-semibold" : ""} data-testid="nav-projects">My projects</button>
          <button onClick={() => dispatch({ type: "GO", view: "reports" })} className={state.view === "reports" ? "font-semibold" : ""} data-testid="nav-reports">My unlocked reports</button>
        </nav>
      </div>
      <div className="flex items-center gap-3">
        <LanguageToggle state={state} dispatch={dispatch} />
        <WalletPill state={state} dispatch={dispatch} />
        <AccountDropdown state={state} dispatch={dispatch} />
      </div>
    </div>
  </header>
);

const LanguageToggle = ({ state, dispatch }) => (
  <div className="hidden sm:flex items-center gap-1 pill text-xs" style={{ background: "var(--paper)", padding: "4px 6px" }} data-testid="lang-toggle">
    <Globe size={11} style={{ color: "var(--muted)" }} />
    <button onClick={() => dispatch({ type: "SET_LANG", language: "en" })} className={`px-2 py-0.5 rounded ${state.language === "en" ? "font-semibold" : ""}`} style={state.language === "en" ? { background: "var(--white)", color: "var(--charcoal)" } : { color: "var(--muted)" }} data-testid="lang-en">EN</button>
    <button onClick={() => dispatch({ type: "SET_LANG", language: "fr" })} className={`px-2 py-0.5 rounded ${state.language === "fr" ? "font-semibold" : ""}`} style={state.language === "fr" ? { background: "var(--white)", color: "var(--charcoal)" } : { color: "var(--muted)" }} data-testid="lang-fr">FR</button>
  </div>
);

const WalletPill = ({ state, dispatch }) => (
  <button
    onClick={() => dispatch({ type: "OPEN_MODAL", modal: { kind: "purchase", startFrom: "wallet" } })}
    className="pill inline-flex items-center gap-1.5 text-sm"
    style={{ background: state.wallet > 0 ? "var(--paper)" : "#FFF6DE", color: state.wallet > 0 ? "var(--charcoal)" : "#5B4A00", cursor: "pointer" }}
    data-testid="wallet-pill"
  >
    <Wallet size={13} />
    {state.wallet === 0 ? "0 report credits · Buy" : `${state.wallet} report credit${state.wallet === 1 ? "" : "s"}`}
  </button>
);

const AccountDropdown = ({ state, dispatch }) => (
  <div className="relative">
    <button onClick={() => dispatch({ type: "TOGGLE_ACCOUNT" })} className="flex items-center gap-2 text-sm hover:opacity-80" data-testid="account-trigger">
      <div className="w-8 h-8 rounded-full flex items-center justify-center font-semibold" style={{ background: "var(--paper)", color: "var(--brand-green)" }}>{SAMPLE_CUSTOMER.initial}</div>
      <span>{SAMPLE_CUSTOMER.name.split(" ")[0]}</span>
      <ChevronDown size={14} />
    </button>
    {state.accountOpen && (
      <div className="absolute right-0 top-11 z-30 w-64 p-2 rounded-md shadow-xl" style={{ background: "var(--white)", border: "1px solid var(--hairline)" }} data-testid="account-dropdown">
        <div className="p-3 border-b" style={{ borderColor: "var(--hairline)" }}>
          <div className="text-sm font-semibold">{SAMPLE_CUSTOMER.name}</div>
          <div className="text-xs" style={{ color: "var(--muted)" }}>{SAMPLE_CUSTOMER.email}</div>
        </div>
        <div className="py-1">
          <DdItem icon={User}      label="Profile"                   testid="dd-profile" onClick={() => dispatch({ type: "TOAST", msg: "Open your Profile page from the app to edit your details." })} />
          <DdItem icon={Wallet}    label={`Report-credit wallet · ${state.wallet}`} testid="dd-wallet"  onClick={() => dispatch({ type: "TOAST", msg: `You have ${state.wallet} report credit${state.wallet === 1 ? "" : "s"}` })} />
          <DdItem icon={CreditCardIcon} label="Buy report credits"  testid="dd-buy"     onClick={() => dispatch({ type: "OPEN_MODAL", modal: { kind: "purchase", startFrom: "wallet" } })} />
          <DdItem icon={FileText}  label="My unlocked reports"       testid="dd-reports" onClick={() => dispatch({ type: "GO", view: "reports" })} />
          <DdItem icon={ShieldCheck} label="Profile settings"        testid="dd-settings" onClick={() => dispatch({ type: "TOAST", msg: "Open Profile settings from the app to manage password and notifications." })} />
          <div className="my-1 border-t" style={{ borderColor: "var(--hairline)" }} />
          <DdItem icon={LogOut}    label="Sign out"                  testid="dd-signout" onClick={() => dispatch({ type: "TOAST", msg: "Sign-out — demo only" })} />
        </div>
      </div>
    )}
  </div>
);

const CreditCardIcon = ({ size }) => <Sparkles size={size} />;

const DdItem = ({ icon: Icon, label, onClick, testid }) => (
  <button onClick={onClick} className="w-full text-left flex items-center gap-2 px-3 py-2 text-sm rounded hover:bg-[var(--paper)]" data-testid={testid}>
    <Icon size={13} style={{ color: "var(--muted)" }} />
    {label}
  </button>
);

/* ═══════════════════════════════════════════════════════════════════════════
 *  PROJECTS LIST
 * ═══════════════════════════════════════════════════════════════════════════ */

const ProjectsView = ({ state, dispatch }) => (
  <>
    <div className="flex justify-between items-end gap-4 flex-wrap mb-8">
      <div>
        <div className="overline mb-2">Saved</div>
        <h1 className="font-display text-3xl md:text-4xl">My projects</h1>
        {state.wallet > 0 && (
          <p className="text-sm mt-2" style={{ color: "var(--muted)" }}>
            You have <span className="pill" style={{ background: "var(--paper)", color: "var(--brand-green)" }} data-testid="credit-balance-line">{state.wallet} report credit{state.wallet === 1 ? "" : "s"}</span> available.
          </p>
        )}
      </div>
    </div>
    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
      {state.projects.map((p) => (
        <ProjectCard key={p.id} project={p} state={state} dispatch={dispatch} />
      ))}
    </div>
  </>
);

const ProjectCard = ({ project, state, dispatch }) => {
  const latestReport = project.reports[project.reports.length - 1];
  const liveMatchesLatest = latestReport && latestReport.snapshotKey === project.liveSnapshotKey && latestReport.status !== "deleted";
  const openReport = () => dispatch({ type: "GO", view: "project-detail", projectId: project.id });

  const primaryAction = () => {
    if (project.status === "unlocked" && liveMatchesLatest) return openReport();
    if (state.wallet > 0) {
      dispatch({ type: "OPEN_MODAL", modal: { kind: "confirm-unlock", projectId: project.id, projectName: project.name } });
    } else {
      dispatch({ type: "OPEN_MODAL", modal: { kind: "purchase", startFrom: "project", projectId: project.id, projectName: project.name } });
    }
  };

  return (
    <div className="card p-5" data-testid={`project-card-${project.id}`}>
      <div className="flex justify-between items-start">
        <div>
          <div className="font-display text-lg">{project.name}</div>
          <div className="text-xs mt-0.5" style={{ color: "var(--muted)" }}>{project.country} · edited {project.lastEditedAt}</div>
        </div>
        {project.status === "locked" && <span className="pill text-xs" style={{ background: "#FFF6DE", color: "#5B4A00" }}><Lock size={11} /> Locked</span>}
        {project.status === "unlocked" && liveMatchesLatest && <span className="pill text-xs" style={{ background: "var(--paper)", color: "var(--brand-green)" }}><CheckCircle2 size={11} /> Report ready</span>}
        {project.status === "updated" && <span className="pill text-xs" style={{ background: "#FFE7C4", color: "#873F00" }}><AlertCircle size={11} /> New report required</span>}
      </div>

      {project.status === "unlocked" && liveMatchesLatest && latestReport && (
        <div className="mt-3 flex flex-wrap gap-2">
          <button onClick={() => dispatch({ type: "TOAST", msg: `Downloading ${downloadFileName(latestReport, "pdf", state.language)}` })} className="btn-secondary !py-1.5 !px-3 text-xs" data-testid={`btn-pdf-${project.id}`}><Download size={12} /> PDF ({state.language.toUpperCase()})</button>
          <button onClick={() => dispatch({ type: "TOAST", msg: `Downloading ${downloadFileName(latestReport, "csv", state.language)}` })} className="btn-secondary !py-1.5 !px-3 text-xs" data-testid={`btn-csv-${project.id}`}><Download size={12} /> CSV ({state.language.toUpperCase()})</button>
          <button onClick={openReport} className="btn-secondary !py-1.5 !px-3 text-xs" data-testid={`btn-open-${project.id}`}><ArrowRight size={12} /> Open report</button>
        </div>
      )}

      {project.status === "locked" && (
        <div className="mt-4">
          {state.wallet > 0 && (
            <p className="text-xs mb-2" style={{ color: "var(--muted)" }}>
              You have <b>{state.wallet}</b> report credit{state.wallet === 1 ? "" : "s"} available.
            </p>
          )}
          <button onClick={primaryAction} className="btn-primary w-full justify-center" data-testid={`btn-unlock-${project.id}`}>
            {state.wallet > 0 ? "Unlock with 1 report credit" : "Buy report"}
          </button>
        </div>
      )}

      {project.status === "updated" && (
        <div className="mt-4">
          <p className="text-xs mb-2" style={{ color: "var(--muted)" }}>Existing report version {latestReport?.version} is still available. Updated project needs a new report.</p>
          <div className="flex gap-2">
            <button onClick={primaryAction} className="btn-primary flex-1 justify-center" data-testid={`btn-updated-${project.id}`}>
              {state.wallet > 0 ? "Unlock updated report" : "Buy report"}
            </button>
            <button onClick={openReport} className="btn-secondary !px-3" data-testid={`btn-openold-${project.id}`}>View existing</button>
          </div>
        </div>
      )}

      <div className="mt-3 pt-3 flex flex-wrap gap-3 text-xs" style={{ borderTop: "1px solid var(--hairline)", color: "var(--muted)" }}>
        <button onClick={() => dispatch({ type: "OPEN_MODAL", modal: { kind: "update-warning", projectId: project.id } })} data-testid={`btn-edit-${project.id}`}>Edit calculation inputs</button>
        <button onClick={() => dispatch({ type: "OPEN_MODAL", modal: { kind: "delete-project", projectId: project.id, projectName: project.name } })} data-testid={`btn-delete-project-${project.id}`}>Delete project</button>
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════════════════
 *  UNLOCKED REPORTS (newest-first)
 * ═══════════════════════════════════════════════════════════════════════════ */

const UnlockedReportsView = ({ state, dispatch }) => {
  const rows = useMemo(() => {
    const out = [];
    for (const p of state.projects) {
      for (const r of (p.reports || [])) out.push({ p, r });
    }
    out.sort((a, b) => (b.r.unlockedAt.localeCompare(a.r.unlockedAt)));
    return out;
  }, [state.projects]);

  return (
    <>
      <div className="mb-6">
        <div className="overline mb-2">Library</div>
        <h1 className="font-display text-3xl md:text-4xl">My unlocked reports</h1>
        <p className="text-sm mt-2" style={{ color: "var(--muted)" }}>Every report you have ever unlocked, newest first. Reports remain here even if you delete the live project or one language copy.</p>
      </div>
      <div className="space-y-3">
        {rows.length === 0 && <div className="card p-8 text-center text-sm" style={{ color: "var(--muted)" }}>You have not unlocked any reports yet. Click a locked project to buy your first report.</div>}
        {rows.map(({ p, r }) => (
          <ReportHistoryRow key={`${p.id}-${r.version}`} project={p} report={r} state={state} dispatch={dispatch} />
        ))}
      </div>
    </>
  );
};

const ReportHistoryRow = ({ project, report, state, dispatch }) => {
  const isCurrent = project.status === "unlocked" && report.snapshotKey === project.liveSnapshotKey;
  const isDeleted = report.status === "deleted";
  const availableLangs = ["en", "fr"].filter((L) => report.languages[L] === "ready");
  const preparingLangs = ["en", "fr"].filter((L) => report.languages[L] === "preparing");
  const missingLangs = ["en", "fr"].filter((L) => !report.languages[L]);

  return (
    <div className="card p-5" data-testid={`history-row-${project.id}-v${report.version}`}>
      <div className="flex flex-wrap justify-between items-start gap-3">
        <div>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-display text-lg">{report.projectName}</span>
            <span className="pill text-xs" style={{ background: "var(--paper)" }}>Report version {report.version}</span>
            {isCurrent && !isDeleted && <span className="pill text-xs" style={{ background: "#E4F3E7", color: "var(--brand-green)" }}>Current report</span>}
            {!isCurrent && !isDeleted && <span className="pill text-xs" style={{ background: "#F0EEE8", color: "var(--muted)" }}>Archived snapshot</span>}
            {isDeleted && <span className="pill text-xs" style={{ background: "#FCE7D8", color: "#873F00" }}>Deleted — regenerate free</span>}
          </div>
          <div className="text-xs mt-1" style={{ color: "var(--muted)" }}>
            {report.country} · Unlocked {report.unlockedAt}
            {report.regeneratedAt && ` · Regenerated ${report.regeneratedAt}`}
            {availableLangs.length > 0 && ` · Available: ${availableLangs.map(l => l.toUpperCase()).join(" · ")}`}
            {preparingLangs.length > 0 && ` · Preparing: ${preparingLangs.map(l => l.toUpperCase()).join(" · ")}`}
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          {!isDeleted && availableLangs.map((L) => (
            <button key={L} onClick={() => dispatch({ type: "TOAST", msg: `Downloading ${downloadFileName(report, "pdf", L)}` })} className="btn-secondary !py-1.5 !px-3 text-xs" data-testid={`history-pdf-${project.id}-v${report.version}-${L}`}>
              <Download size={12} /> PDF ({L.toUpperCase()})
            </button>
          ))}
          {!isDeleted && availableLangs.map((L) => (
            <button key={`csv-${L}`} onClick={() => dispatch({ type: "TOAST", msg: `Downloading ${downloadFileName(report, "csv", L)}` })} className="btn-secondary !py-1.5 !px-3 text-xs" data-testid={`history-csv-${project.id}-v${report.version}-${L}`}>
              <Download size={12} /> CSV ({L.toUpperCase()})
            </button>
          ))}
          {!isDeleted && missingLangs.map((L) => (
            <button key={`get-${L}`} onClick={() => {
              dispatch({ type: "REQUEST_TRANSLATION", projectId: project.id, version: report.version, targetLang: L });
              setTimeout(() => dispatch({ type: "TRANSLATION_READY", projectId: project.id, version: report.version, targetLang: L }), 1500);
            }} className="btn-secondary !py-1.5 !px-3 text-xs" data-testid={`history-getlang-${project.id}-v${report.version}-${L}`}>
              Get {L === "fr" ? "French" : "English"} version
            </button>
          ))}
          {isDeleted && report.status !== "regenerating" && (
            <button onClick={() => dispatch({ type: "OPEN_MODAL", modal: { kind: "regenerate", projectId: project.id, version: report.version, projectName: report.projectName, unlockedAt: report.unlockedAt } })}
                    className="btn-primary !py-1.5 !px-3 text-xs" data-testid={`history-regen-${project.id}-v${report.version}`}>
              <RotateCw size={12} /> Regenerate original report
            </button>
          )}
          {report.status === "regenerating" && (
            <span className="pill text-xs inline-flex items-center gap-1" style={{ background: "var(--paper)", color: "var(--muted)" }}><Clock size={11} /> Regenerating report</span>
          )}
          {!isDeleted && (
            <button onClick={() => dispatch({ type: "OPEN_MODAL", modal: { kind: "delete-report", projectId: project.id, version: report.version } })}
                    className="text-xs px-2 py-1.5 rounded hover:bg-[var(--paper)]" data-testid={`history-delete-${project.id}-v${report.version}`}>
              <Trash2 size={12} style={{ color: "var(--muted)" }} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════════════════
 *  PROJECT DETAIL — current + previous reports
 * ═══════════════════════════════════════════════════════════════════════════ */

const ProjectDetailView = ({ project, state, dispatch }) => {
  const latestReport = project.reports[project.reports.length - 1];
  const previousReports = project.reports.slice(0, -1);
  const liveMatchesLatest = latestReport && latestReport.snapshotKey === project.liveSnapshotKey && latestReport.status !== "deleted";

  return (
    <>
      <button onClick={() => dispatch({ type: "GO", view: "projects" })} className="text-sm mb-4" style={{ color: "var(--muted)" }} data-testid="detail-back">← Back to My projects</button>
      <div className="flex items-start justify-between gap-4 flex-wrap mb-6">
        <div>
          <div className="overline mb-1">{project.country}</div>
          <h1 className="font-display text-3xl">{project.name}</h1>
        </div>
        {liveMatchesLatest && <span className="pill text-xs" style={{ background: "var(--paper)", color: "var(--brand-green)" }}><CheckCircle2 size={11} /> Current report is up to date</span>}
        {project.status === "updated" && <span className="pill text-xs" style={{ background: "#FFE7C4", color: "#873F00" }}><AlertCircle size={11} /> Updated, new report required</span>}
      </div>

      {latestReport && (
        <div className="card p-5">
          <div className="overline">{liveMatchesLatest ? "Current report" : "Latest report"}</div>
          <ReportHistoryRow project={project} report={latestReport} state={state} dispatch={dispatch} />
        </div>
      )}

      {previousReports.length > 0 && (
        <>
          <div className="overline mt-8 mb-3">Previous reports</div>
          <div className="space-y-3">
            {previousReports.slice().reverse().map((r) => (
              <ReportHistoryRow key={r.version} project={project} report={r} state={state} dispatch={dispatch} />
            ))}
          </div>
        </>
      )}
    </>
  );
};

/* ═══════════════════════════════════════════════════════════════════════════
 *  MODALS
 * ═══════════════════════════════════════════════════════════════════════════ */

const Modals = ({ state, dispatch }) => {
  const m = state.activeModal;
  if (!m) return null;
  const close = () => dispatch({ type: "CLOSE_MODAL" });
  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center p-4" style={{ background: "rgba(22,33,29,0.55)" }}>
      <div className="w-full max-w-lg rounded-lg shadow-xl relative" style={{ background: "var(--white)", maxHeight: "90vh", overflowY: "auto" }} data-testid={`modal-${m.kind}`}>
        <button onClick={close} className="absolute top-4 right-4 z-10" style={{ color: "var(--muted)" }} data-testid="modal-close"><X size={18} /></button>
        {m.kind === "purchase" && <PurchaseModal state={state} dispatch={dispatch} modal={m} />}
        {m.kind === "confirm-unlock" && <ConfirmUnlockModal state={state} dispatch={dispatch} modal={m} />}
        {m.kind === "update-warning" && <UpdateWarningModal state={state} dispatch={dispatch} modal={m} />}
        {m.kind === "delete-report" && <DeleteReportModal state={state} dispatch={dispatch} modal={m} />}
        {m.kind === "delete-project" && <DeleteProjectModal state={state} dispatch={dispatch} modal={m} />}
        {m.kind === "regenerate" && <RegenerateModal state={state} dispatch={dispatch} modal={m} />}
        {m.kind === "receipt" && <ReceiptModal state={state} dispatch={dispatch} modal={m} />}
        {m.kind === "ready-email" && <ReadyEmailModal state={state} dispatch={dispatch} modal={m} />}
        {m.kind === "translated-email" && <TranslatedEmailModal state={state} dispatch={dispatch} modal={m} />}
        {m.kind === "regen-email" && <RegenEmailModal state={state} dispatch={dispatch} modal={m} />}
      </div>
    </div>
  );
};

const PurchaseModal = ({ state, dispatch, modal }) => {
  const startedFromWallet = modal.startFrom === "wallet";
  const [selectedProjectId, setSelectedProjectId] = useState(modal.projectId || null);
  const [choice, setChoice] = useState("bundle"); // "bundle" | "single"
  const lockedProjects = state.projects.filter((p) => p.status === "locked" || p.status === "updated");
  const canSelectSingle = !!selectedProjectId;

  const proceed = () => {
    if (choice === "single") {
      if (!selectedProjectId) return;
      dispatch({ type: "BUY_SINGLE", projectId: selectedProjectId });
    } else {
      dispatch({ type: "BUY_BUNDLE" });
    }
  };

  return (
    <div className="p-8">
      <div className="overline mb-1">Buy report credits</div>
      <h3 className="font-display text-2xl mb-1">Unlock your detailed report</h3>
      <p className="text-sm mb-6" style={{ color: "var(--muted)" }}>Two options. The 5-report bundle is the best value if you plan to run more scopes.</p>

      {startedFromWallet && lockedProjects.length > 0 && (
        <div className="mb-4">
          <label className="text-xs font-semibold uppercase tracking-wider block mb-2" style={{ color: "var(--muted)" }}>Select a locked project (required for the $99 option)</label>
          <select value={selectedProjectId || ""} onChange={(e) => setSelectedProjectId(e.target.value || null)} className="w-full px-3 py-2 text-sm" style={{ background: "var(--white)", border: "1px solid var(--hairline)" }} data-testid="purchase-project-select">
            <option value="">— Choose a project —</option>
            {lockedProjects.map((p) => <option key={p.id} value={p.id}>{p.name} · {p.country}</option>)}
          </select>
        </div>
      )}

      <div className="space-y-3">
        <label className={`flex items-start gap-3 p-4 rounded-md cursor-pointer relative ${choice === "bundle" ? "ring-2" : ""}`} style={{ background: choice === "bundle" ? "var(--paper)" : "var(--white)", border: "1px solid var(--hairline)" }} data-testid="option-bundle">
          <input type="radio" checked={choice === "bundle"} onChange={() => setChoice("bundle")} className="mt-1" />
          <div className="flex-1">
            <div className="flex justify-between items-baseline flex-wrap gap-2">
              <div className="font-display text-lg">5 reports</div>
              <div className="font-display text-xl">$375</div>
            </div>
            <div className="text-xs mt-1" style={{ color: "var(--muted)" }}>= $75 per report · <span style={{ color: "var(--brand-green)", fontWeight: 600 }}>Best Value · Save $120</span> vs 5 × $99</div>
            <div className="text-xs mt-2" style={{ color: "var(--body)" }}>Adds 5 report credits to your wallet. Credits never expire. Use one credit to unlock any future project.</div>
          </div>
        </label>

        <label className={`flex items-start gap-3 p-4 rounded-md cursor-pointer ${choice === "single" ? "ring-2" : ""} ${!canSelectSingle && choice === "single" ? "opacity-50" : ""}`} style={{ background: choice === "single" ? "var(--paper)" : "var(--white)", border: "1px solid var(--hairline)" }} data-testid="option-single">
          <input type="radio" checked={choice === "single"} onChange={() => setChoice("single")} className="mt-1" disabled={!canSelectSingle && startedFromWallet} />
          <div className="flex-1">
            <div className="flex justify-between items-baseline flex-wrap gap-2">
              <div className="font-display text-lg">Detailed report</div>
              <div className="font-display text-xl">$99</div>
            </div>
            <div className="text-xs mt-1" style={{ color: "var(--muted)" }}>Automatically unlocks the project you selected. No credit added to your wallet.</div>
            {startedFromWallet && !canSelectSingle && <div className="text-xs mt-2" style={{ color: "#873F00" }}>Please select a project above to use this option.</div>}
          </div>
        </label>
      </div>

      <div className="mt-6 p-4 rounded-md flex justify-between items-center" style={{ background: "var(--paper)" }} data-testid="purchase-summary">
        <div>
          <div className="text-xs uppercase tracking-wider" style={{ color: "var(--muted)" }}>You pay</div>
          <div className="font-display text-2xl">${choice === "bundle" ? 375 : 99}</div>
        </div>
        <div className="text-right">
          <div className="text-xs uppercase tracking-wider" style={{ color: "var(--muted)" }}>You get</div>
          <div className="font-display text-sm">{choice === "bundle" ? "5 report credits" : "1 report unlocked"}</div>
          {choice === "bundle" && <div className="text-xs" style={{ color: "var(--brand-green)" }}>Save $120</div>}
        </div>
      </div>

      <div className="mt-6 flex gap-3 justify-end">
        <button onClick={() => dispatch({ type: "CLOSE_MODAL" })} className="btn-secondary" data-testid="purchase-cancel">Cancel</button>
        <button onClick={proceed} disabled={choice === "single" && !selectedProjectId} className="btn-primary" data-testid="purchase-confirm">
          {choice === "bundle" ? "Buy 5 reports · $375" : "Buy the report · $99"}
        </button>
      </div>
    </div>
  );
};

const ConfirmUnlockModal = ({ state, dispatch, modal }) => (
  <div className="p-8">
    <div className="overline mb-1">Confirm unlock</div>
    <h3 className="font-display text-2xl mb-4">Use 1 report credit?</h3>
    <p className="text-sm mb-6" style={{ color: "var(--body)" }}>
      Unlock the detailed report for <b>{modal.projectName}</b>.
      <br /><b>{state.wallet - 1}</b> report credit{state.wallet - 1 === 1 ? "" : "s"} will remain in your wallet after this.
    </p>
    <div className="flex gap-3 justify-end">
      <button onClick={() => dispatch({ type: "CLOSE_MODAL" })} className="btn-secondary" data-testid="confirm-cancel">Cancel</button>
      <button onClick={() => dispatch({ type: "USE_CREDIT", projectId: modal.projectId })} className="btn-primary" data-testid="confirm-unlock">Unlock report</button>
    </div>
  </div>
);

const UpdateWarningModal = ({ dispatch, modal }) => (
  <div className="p-8">
    <div className="overline mb-1">Project change</div>
    <h3 className="font-display text-2xl mb-4">Update this project?</h3>
    <p className="text-sm mb-6" style={{ color: "var(--body)" }}>
      This change will update your project. Your existing report will remain available, but you will need a new report credit to unlock a report for the updated version.
    </p>
    <div className="flex gap-3 justify-end">
      <button onClick={() => dispatch({ type: "CLOSE_MODAL" })} className="btn-secondary" data-testid="update-cancel">Cancel changes</button>
      <button onClick={() => dispatch({ type: "EDIT_PROJECT", projectId: modal.projectId })} className="btn-primary" data-testid="update-save">Save changes</button>
    </div>
  </div>
);

const DeleteReportModal = ({ dispatch, modal }) => (
  <div className="p-8">
    <div className="overline mb-1">Delete report</div>
    <h3 className="font-display text-2xl mb-4">Delete Report version {modal.version}?</h3>
    <p className="text-sm mb-6" style={{ color: "var(--body)" }}>
      This will remove all PDF, CSV, and language copies for this report version. This cannot be undone and does not restore a report credit. You can regenerate the same version for free anytime.
    </p>
    <div className="flex gap-3 justify-end">
      <button onClick={() => dispatch({ type: "CLOSE_MODAL" })} className="btn-secondary" data-testid="deletereport-cancel">Cancel</button>
      <button onClick={() => dispatch({ type: "DELETE_REPORT", projectId: modal.projectId, version: modal.version })} className="btn-primary" style={{ background: "#8B2323", borderColor: "#8B2323" }} data-testid="deletereport-confirm">Delete report</button>
    </div>
  </div>
);

const DeleteProjectModal = ({ dispatch, modal }) => (
  <div className="p-8">
    <div className="overline mb-1">Delete project</div>
    <h3 className="font-display text-2xl mb-4">Delete "{modal.projectName}"?</h3>
    <p className="text-sm mb-6" style={{ color: "var(--body)" }}>
      The live project will be removed. Paid reports for this project will remain available in <b>My unlocked reports</b> and remain downloadable indefinitely.
    </p>
    <div className="flex gap-3 justify-end">
      <button onClick={() => dispatch({ type: "CLOSE_MODAL" })} className="btn-secondary" data-testid="deleteproject-cancel">Cancel</button>
      <button onClick={() => dispatch({ type: "DELETE_PROJECT", projectId: modal.projectId })} className="btn-primary" style={{ background: "#8B2323", borderColor: "#8B2323" }} data-testid="deleteproject-confirm">Delete project</button>
    </div>
  </div>
);

const RegenerateModal = ({ state, dispatch, modal }) => (
  <div className="p-8">
    <div className="overline mb-1">Regenerate report</div>
    <h3 className="font-display text-2xl mb-4">Regenerate Report version {modal.version}?</h3>
    <p className="text-sm mb-6" style={{ color: "var(--body)" }}>
      This will recreate Report version {modal.version} for <b>{modal.projectName}</b> from its original saved configuration snapshot (unlocked {modal.unlockedAt}). It will not use any changes made to the live project since this report was first unlocked. Free of charge, uses no report credit.
    </p>
    <div className="flex gap-3 justify-end">
      <button onClick={() => dispatch({ type: "CLOSE_MODAL" })} className="btn-secondary" data-testid="regen-cancel">Cancel</button>
      <button
        onClick={() => {
          dispatch({ type: "REGENERATE_START", projectId: modal.projectId, version: modal.version });
          setTimeout(() => dispatch({ type: "REGENERATE_DONE", projectId: modal.projectId, version: modal.version }), 1800);
        }}
        className="btn-primary" data-testid="regen-confirm">
        Regenerate original report
      </button>
    </div>
  </div>
);

const ReceiptModal = ({ state, dispatch, modal }) => {
  const project = modal.projectId ? state.projects.find((p) => p.id === modal.projectId) : null;
  const paymentRef = "PP-DEMO-" + Math.random().toString(36).slice(2, 10).toUpperCase();
  return (
    <div className="p-8">
      <div className="overline mb-1">Preview: Receipt email + PDF</div>
      <h3 className="font-display text-2xl mb-4">Payment confirmed</h3>
      <div className="p-4 rounded-md text-sm space-y-2 mb-4" style={{ background: "var(--paper)" }} data-testid="receipt-body">
        <Row k="Customer" v={`${SAMPLE_CUSTOMER.name} · ${SAMPLE_CUSTOMER.email}`} />
        <Row k="Purchase type" v={modal.purchaseType === "single" ? "Single detailed report" : "5-report bundle"} />
        <Row k="Amount paid" v={`$${modal.amount}.00`} />
        <Row k="Payment date" v={iso(0)} />
        <Row k="Payment reference" v={paymentRef} />
        {project && <Row k="Project" v={project.name} />}
        <Row k="Report credits added" v={modal.creditsAdded} />
        <Row k="Wallet balance now" v={`${state.wallet} report credit${state.wallet === 1 ? "" : "s"}`} />
      </div>
      <div className="text-xs mb-6 space-y-1" style={{ color: "var(--muted)" }}>
        <div><b>PDF receipt seller details</b></div>
        <div>FUTURE ENERGY VENTURE LTD</div>
        <div>c/o Abacus (Seychelles) Limited</div>
        <div>Suite 3, Global Village, Jivan's Complex</div>
        <div>Mont Fleuri, Mahé, Seychelles · P.O. Box 931</div>
      </div>
      <div className="flex gap-3 justify-end">
        <button onClick={() => { dispatch({ type: "CLOSE_MODAL" }); dispatch({ type: "GO", view: modal.projectId ? "project-detail" : "reports", projectId: modal.projectId }); }} className="btn-primary" data-testid="receipt-continue">
          {modal.purchaseType === "single" ? "View your report" : "Go to My unlocked reports"}
        </button>
      </div>
    </div>
  );
};

const ReadyEmailModal = ({ state, dispatch, modal }) => {
  const project = state.projects.find((p) => p.id === modal.projectId);
  const report = project?.reports[project.reports.length - 1];
  return (
    <div className="p-8">
      <div className="overline mb-1">Preview: email · Your report is ready</div>
      <h3 className="font-display text-2xl mb-4">Your report is ready</h3>
      <div className="p-4 rounded-md text-sm space-y-2 mb-6" style={{ background: "var(--paper)" }}>
        <Row k="Project" v={project?.name} />
        <Row k="Report version" v={`v${report?.version}`} />
        <Row k="Unlock date" v={report?.unlockedAt} />
        <Row k="Remaining wallet" v={`${state.wallet} report credit${state.wallet === 1 ? "" : "s"}`} />
      </div>
      <p className="text-xs mb-6" style={{ color: "var(--muted)" }}>No attachments. No receipt. Just a friendly note with a "View your report" button that opens the newest completed report version directly in the app.</p>
      <div className="flex gap-3 justify-end">
        <button onClick={() => { dispatch({ type: "CLOSE_MODAL" }); dispatch({ type: "GO", view: "project-detail", projectId: modal.projectId }); }} className="btn-primary" data-testid="ready-open">View your report</button>
      </div>
    </div>
  );
};

const TranslatedEmailModal = ({ state, dispatch, modal }) => {
  const project = state.projects.find((p) => p.id === modal.projectId);
  return (
    <div className="p-8">
      <div className="overline mb-1">Preview: email · Your translated report is ready</div>
      <h3 className="font-display text-2xl mb-4">Your translated report is ready</h3>
      <div className="p-4 rounded-md text-sm space-y-2 mb-6" style={{ background: "var(--paper)" }}>
        <Row k="Project" v={project?.name} />
        <Row k="Report version" v={`v${modal.version}`} />
        <Row k="Newly available language" v={modal.targetLang === "fr" ? "French (FR)" : "English (EN)"} />
      </div>
      <div className="flex gap-3 justify-end">
        <button onClick={() => { dispatch({ type: "CLOSE_MODAL" }); dispatch({ type: "GO", view: "project-detail", projectId: modal.projectId }); }} className="btn-primary" data-testid="translated-open">View your report</button>
      </div>
    </div>
  );
};

const RegenEmailModal = ({ state, dispatch, modal }) => {
  const project = state.projects.find((p) => p.id === modal.projectId);
  const report = project?.reports.find((r) => r.version === modal.version);
  return (
    <div className="p-8">
      <div className="overline mb-1">Preview: email · Your report has been regenerated</div>
      <h3 className="font-display text-2xl mb-4">Your report has been regenerated</h3>
      <div className="p-4 rounded-md text-sm space-y-2 mb-6" style={{ background: "var(--paper)" }}>
        <Row k="Project" v={project?.name} />
        <Row k="Report version" v={`v${modal.version}`} />
        <Row k="Original unlock date" v={report?.unlockedAt} />
        <Row k="Regeneration date" v={report?.regeneratedAt || iso(0)} />
      </div>
      <div className="flex gap-3 justify-end">
        <button onClick={() => { dispatch({ type: "CLOSE_MODAL" }); dispatch({ type: "GO", view: "project-detail", projectId: modal.projectId }); }} className="btn-primary" data-testid="regen-open">View your report</button>
      </div>
    </div>
  );
};

const Row = ({ k, v }) => (
  <div className="flex justify-between gap-4">
    <span style={{ color: "var(--muted)" }}>{k}</span>
    <span className="font-medium text-right">{v || "—"}</span>
  </div>
);

/* ═══════════════════════════════════════════════════════════════════════════
 *  SCENARIO PANEL (bottom-right, quick jumps for the reviewer)
 * ═══════════════════════════════════════════════════════════════════════════ */

const ScenarioPanel = ({ state, dispatch }) => (
  <div className="fixed bottom-6 left-6 z-40 p-3 rounded-lg shadow-xl w-64 text-xs" style={{ background: "var(--charcoal)", color: "var(--white)", border: "1px solid rgba(255,255,255,0.1)" }} data-testid="scenario-panel">
    <div className="uppercase tracking-wider opacity-70 mb-2">Quick jumps</div>
    <div className="space-y-1">
      <ScenarioButton onClick={() => dispatch({ type: "GO", view: "projects" })} label="1 · My projects (locked)" />
      <ScenarioButton onClick={() => dispatch({ type: "OPEN_MODAL", modal: { kind: "purchase", startFrom: "wallet" } })} label="2 · Purchase pop-up (wallet start)" />
      <ScenarioButton onClick={() => dispatch({ type: "BUY_BUNDLE" })} label="3 · Buy 5-bundle → +5 credits + receipt" />
      <ScenarioButton onClick={() => { const p = state.projects.find(p => p.status === "locked"); if (p) dispatch({ type: "OPEN_MODAL", modal: { kind: "confirm-unlock", projectId: p.id, projectName: p.name } }); else dispatch({ type: "TOAST", msg: "No locked project — reset the demo" }); }} label="4 · Confirm 1-credit unlock" disabled={state.wallet === 0} />
      <ScenarioButton onClick={() => dispatch({ type: "GO", view: "reports" })} label="5 · My unlocked reports" />
      <ScenarioButton onClick={() => { const p = state.projects.find(p => p.reports.length); dispatch({ type: "GO", view: "project-detail", projectId: p?.id }); }} label="6 · Project detail (versions)" />
      <ScenarioButton onClick={() => { const p = state.projects.find(p => p.reports.length); dispatch({ type: "OPEN_MODAL", modal: { kind: "update-warning", projectId: p?.id } }); }} label="7 · Project-update warning" />
      <ScenarioButton onClick={() => { const p = state.projects.find(p => p.reports.length); const r = p?.reports[0]; if (r) dispatch({ type: "OPEN_MODAL", modal: { kind: "delete-report", projectId: p.id, version: r.version } }); }} label="8 · Delete report modal" />
      <ScenarioButton onClick={() => { const p = state.projects.find(p => p.reports.some(r => r.status === "deleted")); if (!p) return dispatch({ type: "TOAST", msg: "Delete a report first (scenario 8)" }); const r = p.reports.find(r => r.status === "deleted"); dispatch({ type: "OPEN_MODAL", modal: { kind: "regenerate", projectId: p.id, version: r.version, projectName: p.name, unlockedAt: r.unlockedAt } }); }} label="9 · Regenerate modal" />
    </div>
    <div className="mt-3 pt-2 border-t border-white/10 opacity-70">Wallet: <b>{state.wallet}</b> credit{state.wallet === 1 ? "" : "s"} · Lang: <b>{state.language.toUpperCase()}</b></div>
  </div>
);

const ScenarioButton = ({ onClick, label, disabled }) => (
  <button onClick={onClick} disabled={disabled} className={`block w-full text-left px-2 py-1 rounded ${disabled ? "opacity-40" : "hover:bg-white/10"}`}>
    {label}
  </button>
);

/* ═══════════════════════════════════════════════════════════════════════════
 *  HELPERS
 * ═══════════════════════════════════════════════════════════════════════════ */

function downloadFileName(report, ext, lang) {
  const safe = (report.projectName || "Report").replace(/\s+/g, "-").replace(/[^A-Za-z0-9-]/g, "");
  const langCode = lang.toUpperCase();
  const date = report.regeneratedAt || report.unlockedAt || iso(0);
  const regenTag = report.regeneratedAt ? `Regenerated-${date}` : date;
  return `${safe}_Report-v${report.version}_${langCode}_${regenTag}.${ext}`;
}
